/**
 * Transcript UI Store
 *
 * Centralises all transcript / language-learning UI state so it survives
 * panel open/close (component unmounts) and is shared between the desktop
 * TranscriptPanel and the mobile MobilePlayerFullscreen.
 *
 * Persisted to localStorage via zustand/middleware persist so preferences
 * and in-progress translation jobs are restored on refresh or panel re-open.
 *
 * Per-podcast translation tracking: `translatingByPodcast` maps
 * podcastId → language-code so switching podcasts never leaks state.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

interface TranscriptState {
	// ── User preferences (shared across all podcasts) ───────────────────────

	/** Whether the dual-language (language-learning) column is visible. */
	isDualLanguage: boolean;

	/**
	 * The translation language currently shown in the dual column.
	 * ISO 639-1 code, e.g. "en", "fr".
	 */
	translationLangCode: string;

	// ── Per-podcast translation jobs ────────────────────────────────────────

	/**
	 * Maps podcastId → language code that is currently being translated.
	 * A missing / null entry means no translation is in progress for that podcast.
	 */
	translatingByPodcast: Record<string, string>;

	// ── Actions ─────────────────────────────────────────────────────────────

	setIsDualLanguage: (v: boolean) => void;
	setTranslationLangCode: (lang: string) => void;

	/** Mark a language as being generated for a given podcast. */
	startTranslating: (podcastId: string, lang: string) => void;

	/** Call when translation has arrived or on error. */
	stopTranslating: (podcastId: string) => void;

	/**
	 * Shorthand: translation complete → set translationLangCode, enable dual
	 * language mode, and clear the in-progress marker for this podcast.
	 */
	completeTranslation: (podcastId: string, lang: string) => void;

	/** Returns the in-progress language for a podcast (undefined if idle). */
	getTranslatingLang: (podcastId: string) => string | undefined;
}

export const useTranscriptStore = create<TranscriptState>()(
	persist(
		(set, get) => ({
			isDualLanguage: false,
			translationLangCode: "en",
			translatingByPodcast: {},

			setIsDualLanguage: (v) => set({ isDualLanguage: v }),

			setTranslationLangCode: (lang) => set({ translationLangCode: lang }),

			startTranslating: (podcastId, lang) =>
				set((s) => ({
					translatingByPodcast: {
						...s.translatingByPodcast,
						[podcastId]: lang,
					},
				})),

			stopTranslating: (podcastId) =>
				set((s) => {
					const next = { ...s.translatingByPodcast };
					delete next[podcastId];
					return { translatingByPodcast: next };
				}),

			completeTranslation: (podcastId, lang) =>
				set((s) => {
					const next = { ...s.translatingByPodcast };
					delete next[podcastId];
					return {
						translatingByPodcast: next,
						translationLangCode: lang,
						isDualLanguage: true,
					};
				}),

			getTranslatingLang: (podcastId) => get().translatingByPodcast[podcastId],
		}),
		{
			name: "podhive-transcript-ui",
			// Only persist preferences + in-progress jobs; nothing sensitive
			partialize: (s) => ({
				isDualLanguage: s.isDualLanguage,
				translationLangCode: s.translationLangCode,
				translatingByPodcast: s.translatingByPodcast,
			}),
		},
	),
);

/** Convenience accessor for actions without a subscription. */
export const getTranscriptActions = () => {
	const s = useTranscriptStore.getState();
	return {
		setIsDualLanguage: s.setIsDualLanguage,
		setTranslationLangCode: s.setTranslationLangCode,
		startTranslating: s.startTranslating,
		stopTranslating: s.stopTranslating,
		completeTranslation: s.completeTranslation,
	};
};
